/**
 * KnowledgeStatCard — vibe-designer 1.169.0 Phase 2.
 *
 * Reusable stat tile for the Knowledge tab Overview grid. Named with
 * the Knowledge prefix to avoid potential collisions (platform-dashboard
 * has its own StatCard; vibe-designer's stays scope-safe).
 *
 * Honest "—" for null values; greyed-out when unavailable.
 */
import clsx from 'clsx';
import { Info } from 'lucide-react';
import { SERVICE_META, type KnowledgeServiceKey } from './serviceMeta';

interface KnowledgeStatCardProps {
  label: string;
  value: number | null;
  sublabel?: string;
  available: boolean;
  /** 2026-06-29 redesign: per-service identity (colored top band + icon).
   * Optional so callers that don't map to a knowledge system still render. */
  service?: KnowledgeServiceKey;
  /** 1.169.0 Phase 2 R2 C4: surface honest error in the overview grid.
   * When set, an ⓘ icon appears next to the availability dot with the
   * error message as a tooltip — distinguishes COGNITION's permanent
   * auth-gap "—" from a transient failure "—". */
  error?: string | null;
}

export function KnowledgeStatCard({
  label,
  value,
  sublabel,
  available,
  service,
  error,
}: KnowledgeStatCardProps) {
  const testid = `knowledge-stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const meta = service ? SERVICE_META[service] : undefined;
  const ServiceIcon = meta?.Icon;
  // Phase 8.0 P1-B: dim only the LABEL/VALUE/SUBLABEL when unavailable —
  // keep the availability dot + info icon at full opacity. Pre-fix
  // `opacity-50` on the wrapper dimmed EVERYTHING including the dot,
  // which is the primary status signal. The dot must stay visible so
  // users can tell "unavailable" from "loading."
  const dimContent = !available;
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-vibe-border-strong bg-vibe-surface shadow-vibe-sm overflow-hidden flex flex-col"
    >
      {/* Per-service accent band — the card's color identity. Neutral when no
          service is mapped. Dimmed when the service is offline. */}
      <div
        data-testid="knowledge-stat-card-band"
        className={clsx('h-1', meta ? meta.dotClass : 'bg-vibe-border', dimContent && 'opacity-40')}
      />
      <div className="p-4 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span
          className={clsx(
            'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary',
            dimContent && 'opacity-50',
          )}
        >
          {ServiceIcon && <ServiceIcon className={clsx('w-3.5 h-3.5', meta?.textClass)} />}
          {label}
        </span>
        <div className="flex items-center gap-1.5">
          {error && (
            <span
              data-testid="knowledge-stat-card-info"
              title={error}
              aria-label={`info: ${error}`}
              className="text-vibe-warning"
            >
              <Info className="w-3 h-3" />
            </span>
          )}
          <span
            data-testid="knowledge-stat-card-dot"
            className={clsx(
              'inline-block w-2 h-2 rounded-full',
              available ? 'bg-vibe-success' : 'bg-vibe-text-secondary',
            )}
            aria-label={available ? 'available' : 'unavailable'}
          />
        </div>
      </div>
      <div className={clsx('text-2xl font-semibold text-vibe-text tabular-nums', dimContent && 'opacity-50')}>
        {value === null ? '—' : value}
      </div>
      {sublabel && (
        <div className={clsx('text-xs text-vibe-text-secondary', dimContent && 'opacity-50')}>{sublabel}</div>
      )}
      </div>
    </div>
  );
}
